import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ContactHero = () => {
  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (delay) => ({
      opacity: 1,
      y: 0,
      transition: { delay, duration: 0.8, ease: "easeOut" }
    })
  };

  return (
    <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">

      {/* Subtle texture overlay */}
      <div className="absolute inset-0 opacity-10 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] brightness-100 contrast-150 mix-blend-overlay"></div>

      {/* Blobs */}
      <motion.div
        animate={{ scale: [1, 1.15, 1], opacity: [0.15, 0.25, 0.15] }}
        transition={{ duration: 10, repeat: Infinity }}
        className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-500 opacity-15 rounded-full blur-[150px]"
      />
      <motion.div
        animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 8, repeat: Infinity, delay: 2 }}
        className="absolute bottom-0 left-0 w-[700px] h-[700px] bg-amber-500 opacity-10 rounded-full blur-[120px]"
      />

      {/* Rotating decoration */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        className="absolute -top-24 -right-24 w-64 h-64 border border-dashed border-white/10 rounded-full"
      />
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
        className="absolute -bottom-16 -left-16 w-48 h-48 border-2 border-white/5 rounded-full"
      />

      {/* CONTENU */}
      <div className="relative z-10 w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-20 xl:px-32 py-20 lg:py-28">
        <div className="text-center space-y-8">

          {/* Badge */}
          <motion.div
            initial="hidden"
            animate="visible"
            custom={0}
            variants={textVariants}
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-md rounded-full border border-white/15 shadow-lg"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-400"></span>
            </span>
            <span className="text-xs md:text-sm font-medium tracking-wide uppercase text-blue-200">
              Contactez-nous
            </span>
          </motion.div>

          {/* Titre */}
          <motion.h1
            initial="hidden"
            animate="visible"
            custom={0.2}
            variants={textVariants}
            className="text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-tight tracking-tight text-white"
          >
            Parlons de{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-amber-400">
              Votre Projet
            </span>
          </motion.h1>

          {/* Description */}
          <motion.p
            initial="hidden"
            animate="visible"
            custom={0.4}
            variants={textVariants}
            className="text-lg md:text-xl lg:text-2xl text-blue-100/80 leading-relaxed max-w-3xl mx-auto"
          >
            Notre équipe d'experts est prête à vous accompagner dans tous vos besoins professionnels.
            Que ce soit pour l'immobilier, la location de véhicules, ou nos services d'entreprise.
          </motion.p>

          {/* Info badges */}
          <motion.div
            initial="hidden"
            animate="visible"
            custom={0.6}
            variants={textVariants}
            className="flex flex-wrap justify-center gap-6 md:gap-10 pt-6"
          >
            {[
              { icon: "Clock", label: "Horaires", value: "Lun-Ven: 8h-18h" },
              { icon: "MapPin", label: "Localisation", value: "Kinshasa & Lubumbashi" },
              { icon: "Users", label: "Réponse", value: "< 24 heures" }
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/15 flex items-center justify-center">
                  <Icon name={item.icon} size={20} className="text-blue-400" />
                </div>
                <div className="text-left">
                  <p className="text-xs text-blue-300/70 uppercase tracking-wider font-semibold">{item.label}</p>
                  <p className="text-sm md:text-base font-semibold text-white">{item.value}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll Down */}
      <motion.div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 cursor-pointer z-20"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        onClick={() => window.scrollTo({ top: window.innerHeight * 0.7, behavior: 'smooth' })}
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-blue-300/60 font-semibold">Découvrir</span>
          <div className="w-5 h-8 border-2 border-white/20 rounded-full flex justify-center pt-2">
            <motion.div
              className="w-1 h-1 bg-white/50 rounded-full"
              animate={{ y: [0, 8, 0], opacity: [1, 0, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default ContactHero;
